import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Euro, Calculator, Save, FileText, Info } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { BedrijfsafsprakenMelding } from "@/components/BedrijfsafsprakenMelding";
import { TerugNaarVraag } from "@/components/TerugNaarVraag";
import { PageHeader } from "@/pages/Kennisbank";
import { toast } from "sonner";

const Salarischeck = () => {
  const location = useLocation();
  const [opties, setOpties] = useState(null);
  const [fg, setFg] = useState("");
  const [periodiek, setPeriodiek] = useState("0");
  const [uren, setUren] = useState("36");
  const [ploeg, setPloeg] = useState("dagdienst");
  const [uitbetaling, setUitbetaling] = useState("maand");
  const [deeltijd, setDeeltijd] = useState("");
  const [peildatum, setPeildatum] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [vanFunctiecheck, setVanFunctiecheck] = useState(false);

  useEffect(() => { api.get("/salarischeck/opties").then((r) => setOpties(r.data)); }, []);

  useEffect(() => {
    const g = location.state?.functiegroep;
    if (g) { setFg(String(g)); setPeriodiek("0"); setVanFunctiecheck(true); }
  }, [location.state]);

  const periodiekCount = opties?.periodieken?.[fg] || 0;
  const isPeriode = uitbetaling === "periode";
  const naarPeriode = (v) => (isPeriode ? Math.round((v * 12) / 13) : v);
  const periodeLabel = isPeriode ? "per 4-wekenperiode" : "per maand";

  const bereken = async () => {
    if (!fg) { toast.error("Kies een functiegroep."); return; }
    setBusy(true);
    try {
      const res = await api.post("/salarischeck", {
        functiegroep: fg, periodiek: parseInt(periodiek), contracturen: parseFloat(uren),
        ploegenrooster: ploeg,
        deeltijdpercentage: deeltijd ? parseFloat(deeltijd) : null,
        peildatum: peildatum || null,
      });
      setResult(res.data);
    } finally { setBusy(false); }
  };

  const save = async () => {
    await api.post("/checks/save", {
      type_check: "salarischeck",
      invoerwaarden: { functiegroep: fg, periodiek, contracturen: uren, ploegendienst: ploeg },
      uitkomst: result,
    });
    toast.success("Salarischeck opgeslagen in je persoonlijke omgeving.");
  };

  return (
    <div className="animate-fade-up">
      <TerugNaarVraag />
      <PageHeader title="Salarischeck" icon={Euro}
        accent={{ chip: "bg-emerald-600", bar: "from-emerald-400 to-emerald-700", text: "text-emerald-600" }}
        subtitle="Bereken het indicatieve cao-salaris op basis van de salaristabellen uit de Cao Zoetwaren. De uitkomst is een indicatie, geen oordeel over je werkgever." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          {vanFunctiecheck && (
            <div data-testid="salaris-functiecheck-melding" className="bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-3 text-sm flex gap-2 items-start">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>De functiegroep is automatisch ingevuld op basis van de functie-indicatie uit het functiehandboek. Controleer of deze overeenkomt met uw formele functie-indeling.</span>
            </div>
          )}
          <div>
            <Label>Functiegroep</Label>
            <Select value={fg} onValueChange={(v) => { setFg(v); setPeriodiek("0"); }}>
              <SelectTrigger data-testid="salaris-functiegroep"><SelectValue placeholder="Kies functiegroep" /></SelectTrigger>
              <SelectContent>
                {opties?.functiegroepen?.map((g) => <SelectItem key={g} value={g}>Functiegroep {g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Periodiek</Label>
            <Select value={periodiek} onValueChange={setPeriodiek} disabled={!fg}>
              <SelectTrigger data-testid="salaris-periodiek"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: periodiekCount }).map((_, i) => (
                  <SelectItem key={i} value={String(i)}>{i === 0 ? "Aanvangsperiodiek (0)" : `Periodiek ${i}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contracturen per week</Label>
            <Input data-testid="salaris-uren" type="number" value={uren} onChange={(e) => setUren(e.target.value)} />
            <p className="text-xs text-slate-400 mt-1">Fulltime = {opties?.fulltime_uren || 36} uur. Gebruik de contracturen zoals ze op je arbeidsovereenkomst of loonstrook staan; ADV/ATV-compensatie-uren tel je niet mee.</p>
          </div>
          <div>
            <Label>Ploegenrooster</Label>
            <Select value={ploeg} onValueChange={setPloeg}>
              <SelectTrigger data-testid="salaris-ploeg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(opties?.ploegenroosters || []).map((r) => (
                  <SelectItem key={r.waarde} value={r.waarde}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">In de zoetwarensector bepaalt het rooster vaak een aanzienlijke toeslag.</p>
          </div>
          <div>
            <Label>Uitbetalingswijze</Label>
            <Select value={uitbetaling} onValueChange={setUitbetaling}>
              <SelectTrigger data-testid="salaris-uitbetaling"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="maand">Maandloon (12 betalingen)</SelectItem>
                <SelectItem value="periode">Periodebetaling (13 periodes van 4 weken)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">Kies zoals op je loonstrook, om vergelijken makkelijker te maken.</p>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-500 font-medium">Optionele gegevens (voor uitleg)</summary>
            <div className="space-y-3 mt-3">
              <div>
                <Label>Deeltijdpercentage (%)</Label>
                <Input data-testid="salaris-deeltijd" type="number" value={deeltijd} onChange={(e) => setDeeltijd(e.target.value)} placeholder="Optioneel" />
              </div>
              <div>
                <Label>Peildatum</Label>
                <Input data-testid="salaris-peildatum" type="date" value={peildatum} onChange={(e) => setPeildatum(e.target.value)} />
              </div>
            </div>
          </details>

          <Button data-testid="salaris-bereken-btn" onClick={bereken} disabled={busy} className="w-full bg-slate-900">
            <Calculator className="w-4 h-4 mr-2" /> {busy ? "Berekenen..." : "Bereken cao-salaris"}
          </Button>
        </div>

        <div>
          {result ? (
            result.success ? (
              <div data-testid="salaris-result" className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 animate-fade-up">
                <StatusBadge status={result.status} />
                <div className="text-center py-2">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Indicatief cao-salaris</p>
                  <p data-testid="salaris-bedrag" className="font-heading text-4xl font-bold text-slate-900 mt-1">
                    &euro; {(uitbetaling === "periode" ? Math.round(result.berekend_salaris * 12 / 13) : result.berekend_salaris).toLocaleString("nl-NL")}
                  </p>
                  <p data-testid="salaris-periode-label" className="text-sm text-slate-500">
                    {uitbetaling === "periode" ? "bruto per 4-wekenperiode" : "bruto per maand"} (excl. roostertoeslag)
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {uitbetaling === "periode"
                      ? `Omgerekend uit € ${result.berekend_salaris.toLocaleString("nl-NL")} per maand (× 12 ÷ 13).`
                      : `Per 4-wekenperiode: € ${Math.round(result.berekend_salaris * 12 / 13).toLocaleString("nl-NL")}.`}
                  </p>
                  <span data-testid="salaris-bruto-badge" className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-slate-100 border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600">
                    <Info className="w-3.5 h-3.5" /> Bruto indicatie – geen netto salarisberekening
                  </span>
                </div>
                <dl className="text-sm space-y-1.5 border-t border-slate-200 pt-3 text-slate-500">
                  <div className="flex justify-between"><dt>Functiegroep</dt><dd className="font-medium text-slate-700">{result.functiegroep}</dd></div>
                  <div className="flex justify-between"><dt>Periodiek</dt><dd className="font-medium text-slate-700">{result.periodiek}</dd></div>
                  <div className="flex justify-between"><dt>Contracturen</dt><dd className="font-medium text-slate-700">{result.contracturen} uur</dd></div>
                  <div className="flex justify-between"><dt>Fulltime salaris</dt><dd className="font-medium text-slate-700">&euro; {result.fulltime_salaris.toLocaleString("nl-NL")}</dd></div>
                  <div className="flex justify-between"><dt>Ploegenrooster</dt><dd className="font-medium text-slate-700">{result.ploegenrooster_label}</dd></div>
                </dl>
                <div data-testid="salaris-roostertoeslag-info" className="text-sm rounded-md p-3 border bg-blue-50 border-blue-200 text-blue-900">
                  {result.roostertoeslag_van_toepassing && result.roostertoeslag?.voorbeeld ? (
                    <div className="space-y-2">
                      <p className="font-semibold flex items-center gap-1.5"><Calculator className="w-4 h-4" /> Indicatieve roostertoeslag</p>
                      {result.roostertoeslag.type === "vast" ? (
                        <div data-testid="salaris-roostertoeslag-bedrag">
                          <p>Voorbeeldpercentage: <span className="font-bold">{String(result.roostertoeslag.percentage).replace(".", ",")}%</span></p>
                          <p>Indicatief toeslagbedrag: <span className="font-bold">&euro; {naarPeriode(result.roostertoeslag.indicatief_bedrag).toLocaleString("nl-NL")}</span> {periodeLabel}</p>
                          <p className="mt-1">Indicatief totaal incl. toeslag: <span className="font-bold">&euro; {naarPeriode(result.berekend_salaris + result.roostertoeslag.indicatief_bedrag).toLocaleString("nl-NL")}</span> {periodeLabel}</p>
                        </div>
                      ) : (
                        <div data-testid="salaris-roostertoeslag-range">
                          <p>CAO-bereik: <span className="font-bold">{String(result.roostertoeslag.range.min).replace(".", ",")}% – {String(result.roostertoeslag.range.max).replace(".", ",")}%</span></p>
                          <p className="text-xs mt-1">De CAO geeft voor dit systeem geen enkel standaardpercentage; daarom tonen we het bereik en geen bedrag.</p>
                        </div>
                      )}
                      <p className="text-xs text-blue-700">Rekenwijze: {result.roostertoeslag.berekeningswijze}</p>
                      <p className="text-xs text-blue-700">Bron: {result.roostertoeslag.voorbeeld}{result.toeslag_bron ? ` · ${result.toeslag_bron.documentnaam} · ${result.toeslag_bron.artikelnummer}` : ""}</p>
                      <p data-testid="salaris-roostertoeslag-disclaimer" className="text-xs italic text-blue-800 border-t border-blue-200 pt-1.5">{result.roostertoeslag.disclaimer}</p>
                    </div>
                  ) : (
                    <>
                      {result.roostertoeslag_info}
                      {result.roostertoeslag_van_toepassing && result.toeslag_bron && (
                        <span className="block mt-1.5 text-xs text-blue-700">
                          Bron: {result.toeslag_bron.documentnaam} · {result.toeslag_bron.artikelnummer} · versie {result.toeslag_bron.versie}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div data-testid="salaris-bruto-toelichting" className="text-sm rounded-md p-3 border bg-slate-50 border-slate-200 text-slate-700">
                  <p className="font-semibold flex items-center gap-1.5"><Info className="w-4 h-4" /> Let op: bruto bedragen</p>
                  <p className="mt-1">Alle genoemde bedragen zijn bruto bedragen. Hierop worden nog inhoudingen toegepast, zoals loonheffing, pensioenpremie, PAWW-premie en eventuele andere werknemersbijdragen. Het netto uitbetaalde bedrag kan daardoor lager uitvallen.</p>
                </div>
                <div data-testid="salaris-indicatie-melding" className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm">{result.indicatie_melding}</div>
                <BedrijfsafsprakenMelding />
                <div className="text-xs text-slate-500 border-l-4 border-slate-200 pl-3 flex items-start gap-1.5">
                  <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{result.bron.documentnaam} · {result.bron.artikelnummer} · gebruikte versie: {result.cao_versie}</span>
                </div>
                <Button data-testid="salaris-save-btn" onClick={save} variant="outline"
                  className="w-full bg-white border-2 border-[#B9772B] text-[#4A3122] hover:bg-[#FBF3E3] hover:text-[#4A3122] focus-visible:ring-2 focus-visible:ring-[#B9772B] focus-visible:ring-offset-2">
                  <Save className="w-4 h-4 mr-2" /> Bewaar deze check
                </Button>
              </div>
            ) : (
              <div data-testid="salaris-error" className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
                <StatusBadge status={result.status} />
                <p className="mt-3 font-medium">{result.melding}</p>
                <p className="text-sm mt-1">{result.detail}</p>
              </div>
            )
          ) : (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400 h-full flex items-center justify-center">
              Vul de gegevens in om je indicatieve cao-salaris te berekenen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Salarischeck;
